import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';

import { usePlaylistColumnData } from './hooks';
import Playlists from './Playlists';

import { FadeIn } from 'animations/FadeIn';
import { RichText } from 'components/Prismic';
import LineClamp from 'components/LineClamp';
import { Normalize } from 'utils/types';

export type TPlaylistColumnSection =
  Normalize<Queries.SolutionDesktopPlaylistColumnData_PrismicSolutionDataDesktopSolutionsPlaylistColumn_Fragment>;

export enum PLAYLIST_SECTION_VARIABLES {
  columnBackgroundColor = '--column-background-color',
  titleColor = '--title-color',
  textColor = '--text-color',
  playlistsTextColor = '--playlists-text-color',
}

enum TEMPLATE_AREAS {
  column = 'column',
  content = 'content',
}

const Container = styled.div<{ isDark: boolean }>`
  ${({ theme: { colors, mq }, isDark }) => css`
    ${PLAYLIST_SECTION_VARIABLES.columnBackgroundColor}: ${isDark
      ? colors.backgroundLightDark
      : colors.white};
    ${PLAYLIST_SECTION_VARIABLES.titleColor}: ${isDark ? colors.primary : '#2A2A2A'};
    ${PLAYLIST_SECTION_VARIABLES.textColor}: ${isDark ? `${colors.white}d9` : '#2A2A2A'};
    ${PLAYLIST_SECTION_VARIABLES.playlistsTextColor}: ${isDark ? colors.white : colors.black};

    display: grid;
    grid-template-areas: '${TEMPLATE_AREAS.column} ${TEMPLATE_AREAS.content}';
    grid-template-columns: 65.625rem 1fr;
    background-color: ${isDark ? '#1F1F1F' : colors.offWhite};
    height: 139.625rem;
    overflow: hidden;

    ${mq.md} {
      grid-template-areas:
        '${TEMPLATE_AREAS.content}'
        '${TEMPLATE_AREAS.column}';
      grid-template-columns: 1fr;
      height: initial;

      && {
        display: grid;
      }
    }
  `}
`;

const Content = styled(FadeIn)`
  ${({ theme: { mq, spacings } }) => css`
    display: grid;
    grid-area: ${TEMPLATE_AREAS.content};
    align-content: center;
    justify-content: center;
    padding: 0 4rem;

    ${mq.md} {
      padding: 7.5rem ${spacings.padding} 6rem;
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;

    & > h2 {
      ${tp.useFont(fonts => fonts.Switzer.lightest)};

      text-align: left;
      line-height: 1.04;
      letter-spacing: -0.07em;
      color: var(${PLAYLIST_SECTION_VARIABLES.titleColor});
      font-size: 13.75rem;
      font-weight: 250;
    }

    ${mq.md} {
      & > h2 {
        font-size: 4rem;
      }
    }
  `}
`;

const Subtitle = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;

    & > h2 {
      text-align: left;
      line-height: 0.965;
      letter-spacing: -0.04em;
      color: var(${PLAYLIST_SECTION_VARIABLES.titleColor});
      font-size: 8rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      strong {
        ${tp.useFont(fonts => fonts.Switzer.light)};
      }
    }

    & > h3 {
      ${tp.useFont(fonts => fonts.Switzer.thin)};

      text-align: left;
      line-height: 1.04;
      letter-spacing: -0.07em;
      color: var(${PLAYLIST_SECTION_VARIABLES.titleColor});
      font-size: 11.125rem;
      font-weight: 150;
    }

    ${mq.md} {
      & > h2 {
        font-size: 4rem;

        strong {
          font-size: 4.25rem;

          ${tp.useFont(fonts => fonts.Switzer.lightest)}; //250
        }
      }

      & > h3 {
        font-size: 4rem;
      }
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;

    & > p {
      ${tp.useFont(fonts => fonts.Switzer.lightest)};

      margin-top: 8rem;
      text-align: left;
      line-height: 1.2;
      color: var(${PLAYLIST_SECTION_VARIABLES.textColor});
      font-size: 3rem;
      font-weight: 225;
    }

    ${mq.md} {
      & > p {
        margin-top: 3rem;
        font-size: 2rem;

        br {
          display: none;
        }
      }
    }
  `}
`;

const StyledPlaylists = styled(Playlists)`
  grid-area: ${TEMPLATE_AREAS.column};
  background-color: var(${PLAYLIST_SECTION_VARIABLES.columnBackgroundColor});
`;

const PlaylistColumnSection: React.FC<TPlaylistColumnSection> = sliceData => {
  const { isDark, title, subtitle, description, playlistsTitle, playlists } =
    usePlaylistColumnData(sliceData);

  return (
    <Container isDark={isDark}>
      <StyledPlaylists title={playlistsTitle} playlists={playlists} />
      <Content>
        <Title render={title} />
        <Subtitle render={subtitle} />
        <LineClamp isDark={!isDark}>
          <Description render={description} />
        </LineClamp>
      </Content>
    </Container>
  );
};

export const desktopPlaylistColumnFragment = graphql`
  fragment SolutionDesktopPlaylistColumnData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionsPlaylistColumn {
      slice_type

      ...SolutionDesktopPlaylistColumnPlaylistsData

      primary {
        solution_playlist_column_is_dark
        solution_playlist_column_title {
          richText
        }
        solution_playlist_column_subtitle {
          richText
        }
        solution_playlist_column_description {
          richText
        }
        solution_playlist_column_playlists_title
      }
    }
  }
`;

export default PlaylistColumnSection;
